import { API } from "./api";

/**
 * Data Loader system props
 */
export interface DataLoaderSystem {
  id?: number;
  name: string;
  system_type: string;
}

/**
 * Data Loader Config
 */
export interface DataLoaderConfig {
  name: string;
  id?: number;
  active_revision_id?: number;
}

export interface DataLoaderConfigRevision {
  id?: number;
  data_loader_config_id: number;
}

export interface DataLoaderConfigRevisionSystems {
  data_loader_config_revision_id: number;
  data_loader_system_id: number;
  write_enabled: boolean;
  read_enabled: boolean;
}

export interface DataOption {
  path?: string;
  name: string;
  description?: string;
  value?: string | number | boolean;
  type?: string;
}

export interface Integration {
  type: string;
  integrations: string[];
}

export interface DataLoaderOption {
  type: string;
  options: DataOption[];
  integrations: Integration[];
}

export interface DataLoaderListOption {
  list_id: string;
  list_name: string;
}

export interface DataLoaderList {
  data_loader_system_id: number;
  list_ids: string[];
}

export interface DataLoaderAdditionalAttribute {
  id?: number;
  entity?: string;
  name: string;
  configuration: string;
}

export interface DataLoaderAdditionalAttributeOption {
  CONTACTS: DataLoaderAdditionalAttribute[];
  ACCOUNTS: DataLoaderAdditionalAttribute[];
}

export interface DataLoaderAdditionalAttributes {
  data_loader_system_id: number;
  additional_attributes: DataLoaderAdditionalAttribute[];
}

export interface IntegrationOptions {
  EmailMarketing?: string;
  TimeAndBilling?: string;
  [key: string]: string | undefined;
}

export interface DataLoaderTypesAndOptionsResponse {
  CRM: DataLoaderOption[];
  SYNC: DataLoaderOption[];
}

export interface StandaloneSyncSystem {
  id: number;
  name: string;
  system_type: string;
  config_revision_id: number;
  item_type: "SYNC";
}

export interface StandaloneSyncSystemPayload {
  system_id?: number;
  config_revision_id?: number;
  name: string;
  sync_system_type: string;
  options: DataOption[];
}

export interface BundledDataLoader {
  DataLoaderConfig: DataLoaderConfig;
  DataLoaderConfigRevision: DataLoaderConfigRevision;
  DataLoaderConfigRevisionSystems: DataLoaderConfigRevisionSystems;
  DataLoaderSystem: DataLoaderSystem;
}

export const getDataLoaderSystems = async (): Promise<DataLoaderSystem[]> => {
  //Get a list of all currently configured data loader systems
  const response = await API.get("get-data-loader-systems");
  return response.data;
};

export const getDataLoaderSystem = async (
  systemId: number
): Promise<DataLoaderSystem> => {
  //Get a list of all currently configured data loader systems
  const response = await API.get("/get-data-loader-system", {
    params: { system_id: systemId },
  });
  return response.data;
};

export const addDataLoaderSystem = async (
  dataLoaderSystem: DataLoaderSystem
): Promise<DataLoaderSystem> => {
  //Create a new data loader system in the CIRROM db
  const response = await API.put("/add-data-loader-system", {
    name: dataLoaderSystem.name,
    system_type: dataLoaderSystem.system_type,
  });
  return response.data;
};

export const addIntegratedDataLoaderSystem = async (
  dataLoaderSystem: DataLoaderSystem
): Promise<DataLoaderSystem> => {
  //Create a new integrated data loader system in the CIRROM db
  const response = await API.put("/add-integrated-data-loader-system", {
    id: dataLoaderSystem.id,
    name: dataLoaderSystem.name,
    system_type: dataLoaderSystem.system_type,
  });
  return response.data;
};

export const addDataLoaderSystemLists = async (
  dataLoaderList: DataLoaderList
) => {
  //Add Data Loader System List Ids
  await API.put("/add-data-system-list-options", {
    data_loader_system_id: dataLoaderList.data_loader_system_id,
    list_ids: dataLoaderList.list_ids,
  });
};

export const updateDataLoaderSystemLists = async (
  dataLoaderList: DataLoaderList
) => {
  //Add Data Loader System List Ids
  await API.put("/update-data-system-list-options", {
    data_loader_system_id: dataLoaderList.data_loader_system_id,
    list_ids: dataLoaderList.list_ids,
  });
};

export interface DataLoaderIntegrations {
  data_loader_id: number;
  config_revision_id: number;
  /** Integration type (e.g. EmailMarketing) -> adaptor name; empty string removes it. */
  integrations: Record<string, string>;
}

export const updateDataLoaderSystemIntegrations = async (
  dataLoaderIntegrations: DataLoaderIntegrations
) => {
  //Add, swap or remove integrated systems on an existing config revision
  await API.put("/update-data-system-integrations", {
    data_loader_id: dataLoaderIntegrations.data_loader_id,
    config_revision_id: dataLoaderIntegrations.config_revision_id,
    integrations: dataLoaderIntegrations.integrations,
  });
};

export const addDataLoaderConfig = async (
  dataLoaderConfig: DataLoaderConfig
): Promise<DataLoaderConfig> => {
  //Create a new data loader config in the CIRROM db
  const response = await API.put("/add-data-loader-config", {
    name: dataLoaderConfig.name,
  });
  return response.data;
};

export const getDataLoaderConfig = async (
  configId: number
): Promise<DataLoaderConfig> => {
  //Create a new data loader config in the CIRROM db
  const response = await API.get("/get-data-loader-config", {
    params: { config_id: configId },
  });
  return response.data;
};

export const getAllDataLoaderConfigs = async (): Promise<
  DataLoaderConfig[]
> => {
  const response = await API.get("/get-all-data-loader-configs");
  return response.data;
};

export const addDataLoaderConfigRevision = async (
  dataLoaderConfigId: number,
  dataLoaderSystemIds: number[],
  readEnabled: boolean,
  writeEnabled: boolean
): Promise<DataLoaderConfig> => {
  //Create a new data loader config revision in the CIRROM db
  const response = await API.put("/add-data-loader-config-revision", {
    data_loader_config_id: dataLoaderConfigId,
    data_loader_system_ids: dataLoaderSystemIds,
    read_enabled: readEnabled,
    write_enabled: writeEnabled,
  });
  return response.data;
};

export const addDataLoaderAdaptorProperties = async (
  dataLoaderSystemId: number,
  configRevisionId: number,
  attributePath: string,
  attributeValue
) => {
  const response = await API.put("/add-data-loader-adaptor-property", {
    data_system_id: dataLoaderSystemId,
    config_revision_id: configRevisionId,
    attribute_path: attributePath,
    attribute_value: attributeValue,
  });
  return response.data;
};

export const updateDataLoaderAdaptorProperties = async (
  dataLoaderSystemId: number,
  configRevisionId: number,
  attributes: DataOption[],
  systemType?: string
) => {
  const response = await API.put("/update-data-loader-adaptor-property", {
    data_system_id: dataLoaderSystemId,
    config_revision_id: configRevisionId,
    attributes: attributes,
    system_type: systemType,
  });
  return response.data;
};

export const getDataLoaderConfigRevision = async (
  configId: number
): Promise<DataLoaderConfigRevision> => {
  //Create a new data loader config revision in the CIRROM db
  const response = await API.get(
    "/get-data-loader-config-revision-for-config",
    {
      params: { config_id: configId },
    }
  );
  return response.data;
};

export const getAllDataLoaderSystems = async (): Promise<
  BundledDataLoader[]
> => {
  const response = await API.get("/get-all-data-loader-systems");
  return response.data;
};

export const getStandaloneSyncSystems = async (): Promise<
  StandaloneSyncSystem[]
> => {
  const response = await API.get("/get-standalone-sync-systems");
  return response.data;
};

export const getReferenceDataSyncSystems = async (
  crmSystemId?: number
): Promise<StandaloneSyncSystem[]> => {
  const response = await API.get("/get-standalone-sync-systems", {
    params: {
      reference_data_only: true,
      ...(crmSystemId != null ? { crm_system_id: crmSystemId } : {}),
    },
  });
  return response.data;
};

export const upsertStandaloneSyncSystem = async (
  syncSystem: StandaloneSyncSystemPayload
): Promise<StandaloneSyncSystem> => {
  const response = await API.put("/upsert-standalone-sync-system", syncSystem);
  return response.data;
};

export const getDataLoaderConfigRevisionSystems = async (
  configRevisionId: number
): Promise<DataLoaderConfigRevisionSystems[]> => {
  const response = await API.get("/get-data-loader-config-revisions-systems", {
    params: { config_revision_id: configRevisionId },
  });
  return response.data;
};

export const getDataLoaderOptions = async (
  dataLoaderId: number,
  configRevisionId: number
): Promise<DataOption[]> => {
  const response = await API.get("/get-options-for-data-loader-and-revision", {
    params: {
      data_loader_id: dataLoaderId,
      config_revision_id: configRevisionId,
    },
  });
  return response.data;
};

export const getIntegratedDataLoader = async (
  dataLoaderId: number,
  configRevisionId: number
): Promise<IntegrationOptions> => {
  const response = await API.get(
    "/get-integrated-system-for-data-loader-and-revision",
    {
      params: {
        data_loader_id: dataLoaderId,
        config_revision_id: configRevisionId,
      },
    }
  );
  return response.data;
};

export const getAvailableDataTypesAndOptions =
  async (): Promise<DataLoaderTypesAndOptionsResponse> => {
    const response = await API.get("/get-data-loader-types-and-options");
    return response.data;
  };

export const getAvailableAdditionalAttributeOptions = async (
  type?: string | null,
  options: {},
  integratedSystemId?: number
): Promise<DataLoaderAdditionalAttributeOption> => {
  const response = await API.post(
    "/get-data-system-additional-attribute-options",
    {
      system_type: type,
      options: options,
      system_id: integratedSystemId,
    }
  );
  return response.data;
};

export const getDataSystemAdditionalAttributes = async (
  data_system_id: number
): Promise<DataLoaderAdditionalAttribute[]> => {
  const response = await API.get("/get-data-system-additional-attributes", {
    params: {
      data_system_id: data_system_id,
    },
  });
  return response.data;
};

export const updateDataLoaderAdditionalAttributes = async (
  dataLoaderAdditionalAttributes: DataLoaderAdditionalAttributes
) => {
  //Add Data Loader System List Ids
  console.log(dataLoaderAdditionalAttributes);
  await API.put(
    "/update-data-system-additional-attributes",
    dataLoaderAdditionalAttributes
  );
};

export const getAvailableListOptions = async (
  type: string,
  options: {},
  integratedSystemId?: number
): Promise<DataLoaderListOption[]> => {
  const response = await API.post("/get-data-system-list-options", {
    system_type: type,
    options: options,
    system_id: integratedSystemId,
  });
  return response.data;
};

export const getDataSystemLists = async (
  data_system_id: number
): Promise<DataLoaderList> => {
  const response = await API.get("/get-data-system-lists", {
    params: {
      data_system_id: data_system_id,
    },
  });
  return response.data;
};

export const getDataSystemEventTypes = async (
  data_system_id: number,
  config_revision_id: number
): Promise<any> => {
  const response = await API.get("/get-data-system-event-types", {
    params: {
      data_system_id: data_system_id,
      config_revision_id: config_revision_id,
    },
  });
  return response.data;
};

export interface MailboxData {
  id: number;
  created_time: string;
  crm_contact_id: number | null;
  crm_createdon: string;
  email: string;
  first_email_received: string;
  last_email_received: string;
  latest_email_body: string;
  latest_email_subject: string;
  mailbox_address: string;
  sender_name: string;
  total_communication: number;
}

export const getMailboxData = async (
  integrated_system_id: number
): Promise<MailboxData[]> => {
  const response = await API.get("/get-mailbox-data", {
    params: {
      integrated_system_id: integrated_system_id,
    },
  });
  return response.data;
};
